import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  Balances,
  EditedRecord,
  PendingTimeOffRequest,
  RequestTypes,
  TimeOffRecord
} from '../../models/TimeOffModels';
import {
  ClientTimeOffRequestService,
  TimeOffRequestService
} from '../../services/time-off-request.service';
import { Injectable } from '@angular/core';
import { mergeMap, tap } from 'rxjs/operators';
import { CalendarEvent } from 'angular-calendar';
import { EventColor } from 'calendar-utils';
import { EmployeePTO, PTOConfig } from '../../models/paid-time-off';
import { CompaniesService } from '../../services/companies.service';
import { ClientSelectionState } from '../ClientSelectionState/ClientSelection.state';
import {
  CountDaysBetweenTwoDates,
  DoesRangeContainWeekends,
  GetHoursBetweenTwoDates,
  GetHoursBetweenTwoTimes
} from '../../utils/dateUtils';
import { DateRange } from '../../models/date-range';
import { eachWeekendOfInterval, Interval, isBefore, isPast, isWeekend, startOfDay } from 'date-fns';
import { formatDatePickerValue, parseDatePickerValue } from '../../utils/datepicker-value-parser';
import { forkJoin, Observable } from 'rxjs';
import { PayDateInfo } from '../../models/payroll';
import { PayrollService } from '../../services/payroll.service';
import { PeopleService } from '../../services/people.service';

export class TimeOffLoadRequests {
  static readonly type = '[TimeOff] Load Requests';

  constructor(
    public clientCode: string,
    public personId: number | null = null
  ) {}
}

export class TimeOffSetFilters {
  static readonly type = '[TimeOff] Set Filters';

  constructor(public newFilters: string[]) {}
}

export class TimeOffRemoveFilter {
  static readonly type = '[TimeOff] Remove Filter';

  constructor(public filterToRemove: string) {}
}

export class TimeOffLoadConfigs {
  static readonly type = '[TimeOff] Load PTO Configs';

  constructor() {}
}

export class TimeOffLoadEmployeePtos {
  static readonly type = '[TimeOff] Load Employee PTOs';

  constructor(public personId: number) {}
}

export class TimeOffLoadBalances {
  static readonly type = '[TimeOff] Load Balances';

  constructor(
    public clientCode: string,
    public personId: number
  ) {}
}

export class TimeOffSetSearchTerm {
  static readonly type = '[TimeOff] Set Search Term';

  constructor(public searchTerm: any | null) {}
}

export class TimeOffSetInitialApprovedRequestBalance {
  static readonly type = '[TimeOff] Set Initial Approved Request Balance';

  constructor(public balance: number | null) {}
}

export class TimeOffSetInitialPendingChangeRequestBalance {
  static readonly type = '[TimeOff] Set Initial Pending Change Request Balance';

  constructor(public balance: number | null) {}
}

export class TimeOffLoadPeriodStartDate {
  static readonly type = '[TimeOff] Load Period Start Date';
}

export interface ITimeOffRequestsState {
  PTOConfigs: PTOConfig[];
  EmployeePTOs: EmployeePTO[];
  Balances: Balances[];
  OpenRequests: PendingTimeOffRequest[];
  ApprovedRequests: TimeOffRecord[];
  DeletedRequests: TimeOffRecord[];
  EditedRequests: EditedRecord[];
  CurrentFilters: string[];
  SearchTerm: any | null;
  InitialApprovedRequestBalance: number | null;
  InitialPendingChangeRequestBalance: number | null;
  PeriodStartDate: Date | null;
  NewRequestForm: {
    model: {
      requestId: number | null;
      timeOffType: number;
      requestType: RequestTypes;
      startDate: {
        year: number;
        month: number;
        day: number;
      };
      hours: number;
      employeeComments: string;
      startTime: string;
      endTime: string;
      dateRange: DateRange;
    };
    dirty: boolean;
    status: string;
    errors: any;
  };
  EmployeeMode: boolean;
}

@State<ITimeOffRequestsState>({
  name: 'timeOffRequests',
  defaults: {
    PTOConfigs: [],
    EmployeePTOs: [],
    Balances: [],
    OpenRequests: [],
    ApprovedRequests: [],
    DeletedRequests: [],
    EditedRequests: [],
    CurrentFilters: ['Requested', 'Pending', 'Approved'],
    SearchTerm: null,
    InitialApprovedRequestBalance: null,
    InitialPendingChangeRequestBalance: null,
    PeriodStartDate: null,
    NewRequestForm: {
      model: null,
      dirty: false,
      status: '',
      errors: {}
    },
    EmployeeMode: true
  }
})
@Injectable()
export class TimeOffRequestsState {
  constructor(
    private employeeTimeOffService: TimeOffRequestService,
    private companiesService: CompaniesService,
    private store: Store,
    private clientTimeOffService: ClientTimeOffRequestService,
    private payrollService: PayrollService,
    private peopleService: PeopleService
  ) {}

  @Selector()
  static getCurrentFilters(state: ITimeOffRequestsState) {
    return state.CurrentFilters;
  }

  @Selector()
  static getBalances(state: ITimeOffRequestsState) {
    return state.Balances;
  }

  @Selector()
  static getOpenRequests(state: ITimeOffRequestsState) {
    return state.OpenRequests;
  }

  @Selector()
  static getApprovedRequests(state: ITimeOffRequestsState) {
    return state.ApprovedRequests;
  }

  @Selector()
  static getDeletedRequests(state: ITimeOffRequestsState) {
    return state.DeletedRequests;
  }

  @Selector()
  static getEditedRequests(state: ITimeOffRequestsState) {
    return state.EditedRequests;
  }

  @Selector()
  static getPTOConfigs(state: ITimeOffRequestsState) {
    return state.PTOConfigs;
  }

  @Selector()
  static getEmployeePtos(state: ITimeOffRequestsState) {
    return state.EmployeePTOs;
  }

  @Selector()
  static isDateRangeWeekend(state: ITimeOffRequestsState) {
    if (state.NewRequestForm == null) return false;
    if (state.NewRequestForm.status == 'INVALID') return false;
    if (state.NewRequestForm.model.requestType == RequestTypes.MultipleDays) {
      const range: Interval = {
        start: state.NewRequestForm.model.dateRange.start,
        end: state.NewRequestForm.model.dateRange.end
      };
      if (!isBefore(range.start, range.end)) {
        return false;
      }
      const weekends = eachWeekendOfInterval(range);
      if (weekends.length > 0) return true;
    } else {
      return isWeekend(parseDatePickerValue(state.NewRequestForm.model.startDate));
    }
    return false;
  }

  @Selector()
  static getFilteredCalendarEvents(state: ITimeOffRequestsState) {
    const events: CalendarEvent[] = [];
    state.DeletedRequests.forEach(x => {
      let color: EventColor;
      let title: string;
      color = {
        primary: '#6AAA37',
        secondary: '#6AAA37'
      };
      title = state.EmployeeMode ? 'Declined' : x.PersonName;
      const startString: any = x.StartDate;
      const startDate = new Date(startString);
      const endString: any = x.EndDate;
      let endDate: Date = null;
      if (endString != null) {
        endDate = new Date(endString);
      }
      events.push({
        title: title,
        color,
        start: startDate,
        end: endDate,
        allDay: x.RequestType != RequestTypes.PartialDay,
        meta: {
          ...x,
          Approved: false,
          Declined: true,
          TimeOffRequestType: x.RequestType
        }
      });
    });
    state.ApprovedRequests.forEach(x => {
      let color: EventColor;
      let title: string;
      color = {
        primary: '#6AAA37',
        secondary: '#6AAA37'
      };
      title = state.EmployeeMode ? 'Approved' : x.PersonName;
      const startString: any = x.StartDate;
      const startDate = new Date(startString);
      const endString: any = x.EndDate;
      let endDate: Date = null;
      if (endString != null) {
        endDate = new Date(endString);
      }
      events.push({
        title: title,
        color,
        start: startDate,
        end: endDate,
        allDay: x.RequestType != RequestTypes.PartialDay,
        meta: {
          ...x,
          Approved: true
        }
      });
    });

    state.EditedRequests.forEach(x => {
      let color: EventColor;
      let title: string;
      color = {
        primary: '#DC3545',
        secondary: '#DC3545'
      };
      title = state.EmployeeMode ? 'Pending Change' : x.EditedRecord.PersonName;
      const startString: any = x.EditedRecord.StartDate;
      const startDate = new Date(startString);
      const endString: any = x.EditedRecord.EndDate;
      let endDate: Date = null;
      if (endString != null) {
        endDate = new Date(endString);
      }
      events.push({
        title: title,
        color,
        start: startDate,
        end: endDate,
        allDay: x.EditedRecord.RequestType != RequestTypes.PartialDay,
        meta: {
          ...x,
          PersonId: x.EditedRecord.PersonId,
          Approved: true,
          EditedByApprover: true
        }
      });
    });
    state.OpenRequests.forEach(x => {
      let color: EventColor;
      let title: string;
      if (x.Pending) {
        title = 'Requested';
      } else if (x.EditedByRequester) {
        title = 'Requested';
      } else if (x.EditedByApprover) {
        title = 'Pending Change';
      } else if (x.Approved) {
        title = 'Approved';
      } else if (x.Declined) {
        title = 'Declined';
      }
      title = state.EmployeeMode ? title : x.RequesterName;
      let startDate: Date = null;
      let endDate: Date = null;

      if (x.Request.TimeOffRequestType == RequestTypes.PartialDay) {
        const startString: any = x.Request.StartTime;
        startDate = new Date(startString);
        const endString: any = x.Request.EndTime;
        endDate = new Date(endString);
      } else {
        const startString: any = x.Request.DateRange?.Start ?? x.Request.StartDate;
        startDate = new Date(startString);
        const endString: any = x.Request.DateRange?.End;
        if (endString != null) {
          endDate = new Date(endString);
        }
      }

      events.push({
        title: title,
        color,
        start: startDate,
        end: endDate,
        allDay: x.Request.TimeOffRequestType != RequestTypes.PartialDay,
        meta: {
          ...x,
          Removed: false
        }
      });
    });
    let filteredEvents: CalendarEvent[] = [];
    state.CurrentFilters.forEach(filter => {
      if (filter == 'Requested') {
        filteredEvents.push(
          ...events.filter(
            x =>
              (x.meta.Pending || x.meta.EditedByRequester) &&
              (state.SearchTerm == null || x.meta.PersonId == state.SearchTerm.Id)
          )
        );
      } else if (filter == 'Pending') {
        filteredEvents.push(
          ...events.filter(
            x =>
              x.meta.EditedByApprover &&
              (state.SearchTerm == null || x.meta.PersonId == state.SearchTerm.Id)
          )
        );
      } else if (filter == 'Approved') {
        filteredEvents.push(
          ...events.filter(
            x =>
              x.meta.Approved &&
              !x.meta.EditedByApprover &&
              (state.SearchTerm == null || x.meta.PersonId == state.SearchTerm.Id)
          )
        );
      } else if (filter == 'Declined') {
        filteredEvents.push(
          ...events.filter(
            x =>
              x.meta.Declined &&
              (state.SearchTerm == null || x.meta.PersonId == state.SearchTerm.Id)
          )
        );
      }
    });
    filteredEvents = filteredEvents.sort((a, b) => {
      if (
        a.meta.TimeOffRequestType == RequestTypes.PartialDay &&
        b.meta.TimeOffRequestType != RequestTypes.PartialDay
      ) {
        return -1;
      }
      if (
        b.meta.TimeOffRequestType == RequestTypes.PartialDay &&
        a.meta.TimeOffRequestType != RequestTypes.PartialDay
      ) {
        return 1;
      }
      if (
        a.meta.TimeOffRequestType == RequestTypes.MultipleDays &&
        b.meta.TimeOffRequestType != RequestTypes.MultipleDays
      ) {
        return 1;
      }
      if (
        a.meta.TimeOffRequestType != RequestTypes.MultipleDays &&
        b.meta.TimeOffRequestType == RequestTypes.MultipleDays
      ) {
        return -1;
      }
      return 0;
    });
    return filteredEvents;
  }

  @Selector()
  static getCollectivePendingChange(state: ITimeOffRequestsState) {
    const openRequestsForActivePlans = TimeOffRequestsState.getOpenRequestsForActivePlans(
      state,
      'PendingChange'
    );
    const editedRequestsForActivePlans: EditedRecord[] = [];
    state.EditedRequests.forEach(request => {
      const config = state.PTOConfigs.find(x => x.Id == request.EditedRecord.PlanId);
      if (config == null) return;
      if (config.IsActive) editedRequestsForActivePlans.push(request);
    });

    return this.calculatePending(openRequestsForActivePlans, editedRequestsForActivePlans);
  }

  @Selector()
  static getCollectiveRequested(state: ITimeOffRequestsState) {
    return this.calculateRequested(this.getOpenRequestsForActivePlans(state, 'Requested'));
  }

  @Selector()
  static getCollectiveAvailableBalance(state: ITimeOffRequestsState) {
    let start = this.getCollectiveBalance(state);
    start = start - this.getCollectiveRequested(state);
    start = start - this.getCollectivePendingChange(state);
    start = start - this.getCollectiveApproved(state);
    return start;
  }

  @Selector()
  static getCollectiveApproved(state: ITimeOffRequestsState) {
    let count = 0;
    state.Balances.filter(x => this.isConfigActive(state.PTOConfigs, x.PlanId)).forEach(x => {
      count += x.Approved;
    });
    return count;
  }

  @Selector()
  static getCollectiveBalance(state: ITimeOffRequestsState) {
    let count = 0;
    state.Balances.filter(x => this.isConfigActive(state.PTOConfigs, x.PlanId)).forEach(x => {
      count += x.Balance;
    });
    return count;
  }

  @Selector()
  static getCurrentRequestHours(state: ITimeOffRequestsState) {
    if (state.NewRequestForm == null) {
      return 0;
    }
    const requestType: RequestTypes = state.NewRequestForm.model.requestType;
    switch (requestType) {
      case RequestTypes.PartialDay:
        return GetHoursBetweenTwoTimes(
          state.NewRequestForm.model.startTime,
          state.NewRequestForm.model.endTime
        );
      case RequestTypes.SingleDay:
        return state.NewRequestForm.model.hours;
      case RequestTypes.MultipleDays:
        let temp =
          state.NewRequestForm.model.hours *
          CountDaysBetweenTwoDates(
            state.NewRequestForm.model.dateRange?.start,
            state.NewRequestForm.model.dateRange?.end
          );
        console.log(temp);
        return temp;
    }
  }

  @Selector()
  static doesRequestContainWeekends(state: ITimeOffRequestsState) {
    let start: Date;
    let end: Date;

    if (state.NewRequestForm.model.requestType != RequestTypes.MultipleDays) {
      if (state.NewRequestForm.model.startDate == null) return false;
      start = parseDatePickerValue(state.NewRequestForm.model.startDate);
      end = start;
    } else {
      if (
        state.NewRequestForm.model.dateRange.start == null ||
        state.NewRequestForm.model.dateRange.end == null
      ) {
        return false;
      }
      start = state.NewRequestForm.model.dateRange.start;
      end = state.NewRequestForm.model.dateRange.end;
    }
    return DoesRangeContainWeekends(start, end);
  }

  @Selector()
  static isDateInPast(state: ITimeOffRequestsState) {
    let start: Date;
    let end: Date;

    if (state.NewRequestForm.model.requestType != RequestTypes.MultipleDays) {
      if (state.NewRequestForm.model.startDate == null) return false;
      start = parseDatePickerValue(state.NewRequestForm.model.startDate);
      end = start;
    } else {
      if (
        state.NewRequestForm.model.dateRange.start == null ||
        state.NewRequestForm.model.dateRange.end == null
      ) {
        return false;
      }
      start = state.NewRequestForm.model.dateRange.start;
      end = state.NewRequestForm.model.dateRange.end;
    }
    return isPast(start) || isPast(end);
  }

  @Selector()
  static getAccruedBalance(state: ITimeOffRequestsState) {
    if (state.NewRequestForm?.model?.timeOffType == null) {
      return 0;
    }
    const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);
    if (balances == null) return 0;
    return balances.Accrued;
  }

  @Selector()
  static getUsedBalance(state: ITimeOffRequestsState) {
    if (state.NewRequestForm?.model?.timeOffType == null) {
      return 0;
    }
    const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);
    if (balances == null) return 0;
    return balances.Used;
  }

  @Selector()
  static getCurrentBalance(state: ITimeOffRequestsState) {
    if (state.NewRequestForm?.model?.timeOffType == null) {
      return 0;
    }
    const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);
    if (balances == null) return 0;
    return balances.Balance;
  }

  @Selector()
  static getApprovedBalance(state: ITimeOffRequestsState) {
    if (state.NewRequestForm?.model?.timeOffType == null) {
      return 0;
    }
    const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);
    if (balances == null) return 0;
    if (state.InitialApprovedRequestBalance != null) {
      return balances.Approved - state.InitialApprovedRequestBalance;
    }
    return balances.Approved;
  }

  @Selector()
  static getPeriodStartDate(state: ITimeOffRequestsState) {
    return formatDatePickerValue(state.PeriodStartDate);
  }

  @Selector()
  static showPeriodStartDateWarning(state: ITimeOffRequestsState) {
    let start: Date;
    let end: Date;

    if (state.NewRequestForm.model.requestType != RequestTypes.MultipleDays) {
      if (state.NewRequestForm.model.startDate == null) return false;
      start = parseDatePickerValue(state.NewRequestForm.model.startDate);
      end = start;
    } else {
      if (
        state.NewRequestForm.model.dateRange.start == null ||
        state.NewRequestForm.model.dateRange.end == null
      ) {
        return false;
      }
      start = state.NewRequestForm.model.dateRange.start;
      end = state.NewRequestForm.model.dateRange.end;
    }
    let finalStart = typeof start == 'string' ? new Date(start) : start;
    let finalEnd = typeof end == 'string' ? new Date(end) : start;
    return (
      isBefore(startOfDay(finalStart), startOfDay(state.PeriodStartDate)) ||
      isBefore(startOfDay(finalEnd), startOfDay(state.PeriodStartDate))
    );
  }

  static getRequestedBalance(personId: number) {
    return createSelector([TimeOffRequestsState], (state: ITimeOffRequestsState) => {
      if (state.NewRequestForm?.model?.timeOffType == null) {
        return 0;
      }
      const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);
      let requests = state.OpenRequests.filter(
        x =>
          x.Request.TimeOffType == balances.PlanId &&
          x.PersonId == personId &&
          (x.Pending || x.EditedByRequester)
      );

      if (state.NewRequestForm.model.requestId != null) {
        requests = requests.filter(x => x.RequestId != state.NewRequestForm.model.requestId);
      }
      return this.calculateRequested(requests);
    });
  }

  static getPendingBalance(personId: number) {
    return createSelector([TimeOffRequestsState], (state: ITimeOffRequestsState) => {
      if (state.NewRequestForm?.model?.timeOffType == null) {
        return 0;
      }
      const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);
      let requests = state.OpenRequests.filter(
        x =>
          x.Request.TimeOffType == balances.PlanId && x.PersonId == personId && x.EditedByApprover
      );
      if (state.NewRequestForm.model.requestId != null) {
        requests = requests.filter(x => x.RequestId != state.NewRequestForm.model.requestId);
      }
      const edited = state.EditedRequests.filter(
        x => x.EditedRecord.PlanId == balances.PlanId && x.EditedRecord.PersonId == personId
      );
      const final = this.calculatePending(requests, edited);
      if (state.InitialPendingChangeRequestBalance != null) {
        return final - state.InitialPendingChangeRequestBalance;
      }
      return final;
    });
  }

  static getAvailableBalance(personId: number) {
    return createSelector([TimeOffRequestsState], (state: ITimeOffRequestsState) => {
      if (state.NewRequestForm?.model?.timeOffType == null) {
        return 0;
      }
      const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);
      let current = balances.Balance;
      current = current - this.getPendingBalance(personId)(state);
      current = current - this.getRequestedBalance(personId)(state);
      current = current - this.getApprovedBalance(state);
      return current;
    });
  }

  static getRemainingBalance(personId: number) {
    return createSelector([TimeOffRequestsState], (state: ITimeOffRequestsState) => {
      if (state.NewRequestForm?.model?.timeOffType == null) {
        return 0;
      }
      const balances = state.Balances.find(x => x.PlanId == state.NewRequestForm.model.timeOffType);

      let current = balances?.Balance ?? 0;
      current = current - this.getPendingBalance(personId)(state);
      current = current - this.getRequestedBalance(personId)(state);
      current = current - this.getApprovedBalance(state);
      current = current - this.getCurrentRequestHours(state);
      return current;
    });
  }

  @Action(TimeOffLoadRequests)
  public loadRequests(
    ctx: StateContext<ITimeOffRequestsState>,
    { clientCode, personId }: TimeOffLoadRequests
  ) {
    const obs =
      personId != null
        ? this.employeeTimeOffService.getRequests(clientCode, personId)
        : this.clientTimeOffService.getRequests(clientCode);
    return obs.pipe(
      tap(result => {
        ctx.patchState({
          OpenRequests: result.Pending ?? [],
          ApprovedRequests: result.Approved ?? [],
          DeletedRequests: result.Deleted ?? [],
          EditedRequests: result.EditedTimeOffRecords ?? [],
          EmployeeMode: personId != null
        });
      })
    );
  }

  @Selector()
  public static getPendingRequestCount(state: ITimeOffRequestsState) {
    return state.OpenRequests.filter(x => x.EditedByRequester || x.Pending).length;
  }

  @Selector()
  public static getPendingChangesCount(state: ITimeOffRequestsState) {
    const open = state.OpenRequests.filter(x => x.EditedByApprover).length;
    const edited = state.EditedRequests.length;
    return open + edited;
  }

  @Selector()
  public static getDeclinedCount(state: ITimeOffRequestsState) {
    const declined = state.OpenRequests.filter(x => x.Declined).length;
    return declined + state.DeletedRequests.length;
  }

  @Selector()
  public static getApprovedCount(state: ITimeOffRequestsState) {
    return state.ApprovedRequests.length;
  }

  @Action(TimeOffSetFilters)
  public setFilters(ctx: StateContext<ITimeOffRequestsState>, { newFilters }: TimeOffSetFilters) {
    ctx.patchState({
      CurrentFilters: newFilters
    });
  }

  @Action(TimeOffRemoveFilter)
  public removeFilter(
    ctx: StateContext<ITimeOffRequestsState>,
    { filterToRemove }: TimeOffRemoveFilter
  ) {
    const state = ctx.getState();
    ctx.patchState({
      CurrentFilters: state.CurrentFilters.filter(x => x != filterToRemove)
    });
  }

  @Action(TimeOffLoadConfigs)
  public loadConfigs(ctx: StateContext<ITimeOffRequestsState>) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(code => {
        return this.companiesService.getPTOConfigs(code);
      }),
      tap(configs => {
        ctx.patchState({
          PTOConfigs: configs
        });
      })
    );
  }

  @Action(TimeOffLoadEmployeePtos)
  public loadEmployeePtos(
    ctx: StateContext<ITimeOffRequestsState>,
    { personId }: TimeOffLoadEmployeePtos
  ) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(code => {
        return this.peopleService.getEmployeePTOs(code, personId);
      }),
      tap(configs => {
        ctx.patchState({
          EmployeePTOs: configs
        });
      })
    );
  }

  @Action(TimeOffLoadBalances)
  public loadBalances(
    ctx: StateContext<ITimeOffRequestsState>,
    { clientCode, personId }: TimeOffLoadBalances
  ) {
    return this.employeeTimeOffService.getBalances(clientCode, personId).pipe(
      tap(balances => {
        ctx.patchState({
          Balances: balances
        });
      })
    );
  }

  @Action(TimeOffSetSearchTerm)
  public setSearchTerm(
    ctx: StateContext<ITimeOffRequestsState>,
    { searchTerm }: TimeOffSetSearchTerm
  ) {
    if (searchTerm == '') {
      searchTerm = null;
    }
    ctx.patchState({
      SearchTerm: searchTerm
    });
  }

  @Action(TimeOffSetInitialApprovedRequestBalance)
  public setInitialApprovedRequestBalance(
    ctx: StateContext<ITimeOffRequestsState>,
    { balance }: TimeOffSetInitialApprovedRequestBalance
  ) {
    ctx.patchState({
      InitialApprovedRequestBalance: balance
    });
  }

  @Action(TimeOffSetInitialPendingChangeRequestBalance)
  public setInitialPendingChangeRequestBalance(
    ctx: StateContext<ITimeOffRequestsState>,
    { balance }: TimeOffSetInitialPendingChangeRequestBalance
  ) {
    ctx.patchState({
      InitialPendingChangeRequestBalance: balance
    });
  }

  @Action(TimeOffLoadPeriodStartDate)
  public fetchPeriodStartDate(ctx: StateContext<ITimeOffRequestsState>) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(code => {
        return this.companiesService.getLocations(code).pipe(
          mergeMap(locs => {
            const nextPayDateObs: Observable<PayDateInfo>[] = [];
            locs.forEach(loc => {
              loc.Calendars.forEach(cal => {
                nextPayDateObs.push(
                  this.payrollService.getNextPayDate(code, loc.Id, cal.CalendarId)
                );
              });
            });
            return forkJoin(nextPayDateObs);
          })
        );
      }),
      tap(nextPayDates => {
        const sorted = nextPayDates.sort((a, b) => {
          if (a.PeriodBegin < b.PeriodBegin) return -1;
          if (a.PeriodBegin > b.PeriodBegin) return 1;
          return 0;
        });
        const stringDate: any = sorted[0].PeriodBegin;
        ctx.patchState({
          PeriodStartDate: new Date(stringDate)
        });
      })
    );
  }

  private static getOpenRequestsForActivePlans(
    state: ITimeOffRequestsState,
    status: 'Requested' | 'PendingChange'
  ) {
    const openRequestsForActivePlans: PendingTimeOffRequest[] = [];
    state.OpenRequests.forEach(request => {
      if (this.isConfigActive(state.PTOConfigs, request.Request.TimeOffType)) {
        if (status == 'Requested' && (request.Pending || request.EditedByRequester)) {
          openRequestsForActivePlans.push(request);
        } else if (status == 'PendingChange' && request.EditedByApprover) {
          openRequestsForActivePlans.push(request);
        }
      }
    });
    return openRequestsForActivePlans;
  }

  private static isConfigActive(configs: PTOConfig[], id: number) {
    const config = configs.find(x => x.Id == id);
    if (config == null) return false;
    return config.IsActive;
  }

  private static calculatePending(requests: PendingTimeOffRequest[], edited: EditedRecord[]) {
    let count = 0;
    requests.forEach(request => {
      count = this.addHoursForRequestType(count, request);
    });
    edited.forEach(request => {
      count = this.addHoursForEditedRequestType(count, request);
    });
    return count;
  }

  private static calculateRequested(requests: PendingTimeOffRequest[]) {
    let count = 0;
    requests.forEach(request => {
      count = this.addHoursForRequestType(count, request);
    });
    return count;
  }

  private static addHoursForRequestType(count: number, request: PendingTimeOffRequest) {
    switch (request.Request.TimeOffRequestType) {
      case RequestTypes.SingleDay:
        count += request.Request.Hours;
        break;
      case RequestTypes.PartialDay:
        const startString: any = request.Request.StartTime;
        const startTime = new Date(startString);
        const endString: any = request.Request.EndTime;
        const endTime = new Date(endString);
        const val = GetHoursBetweenTwoTimes(startTime, endTime);
        count += val;
        break;
      case RequestTypes.MultipleDays:
        count += GetHoursBetweenTwoDates(
          request.Request.DateRange.Start,
          request.Request.DateRange.End,
          request.Request.Hours
        );
        break;
    }
    return count;
  }

  private static addHoursForEditedRequestType(count: number, request: EditedRecord) {
    switch (request.EditedRecord.RequestType) {
      case RequestTypes.SingleDay:
        count += request.EditedRecord.TimeOffDays[0].Hours;
        break;
      case RequestTypes.PartialDay:
        const startString: any = request.EditedRecord.StartDate;
        const startTime = new Date(startString);
        const endString: any = request.EditedRecord.EndDate;
        const endTime = new Date(endString);
        const val = GetHoursBetweenTwoTimes(startTime, endTime);
        count += val;
        break;
      case RequestTypes.MultipleDays:
        count += GetHoursBetweenTwoDates(
          request.EditedRecord.StartDate,
          request.EditedRecord.EndDate,
          request.Request.Hours
        );
        break;
    }
    return count;
  }
}
